import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { socialPlatforms } from '../utils/socialPlatforms';
import { getEmbedUrl, getEmbedLabel } from '../utils/embedUtils';
import { getTheme } from '../utils/themes';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { api, getImageUrl } from '../utils/api';
import { useAuth } from '../App';

const FanPage = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [diaryPosts, setDiaryPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const auth = useAuth();
  const isOwnProfile = auth?.user?.username === username;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileData, posts] = await Promise.all([
          api.getPublicProfile(username),
          api.getPublicDiary(username)
        ]);
        setProfile(profileData);
        setDiaryPosts(posts || []);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fa' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-primary animate-pulse mx-auto mb-4" />
          <p className="text-gray-500">Loading fan page...</p>
        </div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center px-4">
        <Card className="p-8 max-w-md w-full text-center">
          <MonkeyLogo size={40} className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
          <p className="text-muted-foreground mb-6">This fan page doesn't exist.</p>
          <Link to="/"><Button>Go to Homepage</Button></Link>
        </Card>
      </div>
    );
  }

  const theme = getTheme(profile.theme);
  const ts = theme.styles;
  const profilePicUrl = getImageUrl(profile.profilePicture);
  const displayName = profile.name || username;

  return (
    <div className="min-h-screen" style={{ background: ts['--theme-gradient'], backgroundColor: ts['--theme-bg'] }} data-testid="fan-page">
      <Helmet>
        <title>{`${displayName}'s Fan Page - LinkMonkey`}</title>
        <meta name="description" content={`Check out what ${displayName} is into right now`} />
      </Helmet>

      {/* Header */}
      <div className="sticky top-0 z-50 px-4 py-3 flex items-center justify-between" style={{ background: `${ts['--theme-bg']}cc`, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${ts['--theme-card-border']}` }}>
        <Link to={`/${username}`} className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity" data-testid="back-to-profile">
          <ArrowLeft className="w-4 h-4" style={{ color: ts['--theme-text'] }} />
          <span className="text-sm font-medium" style={{ color: ts['--theme-text'] }}>Back to profile</span>
        </Link>
        {isOwnProfile && (
          <Link to="/settings">
            <Button size="sm" variant="outline" className="text-xs" style={{ borderColor: ts['--theme-accent'], color: ts['--theme-accent'] }}>
              Manage Posts
            </Button>
          </Link>
        )}
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6 pb-20">
        {/* Mini profile header */}
        <div className="flex items-center gap-3 mb-6">
          {profilePicUrl ? (
            <img src={profilePicUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover"
              style={{ border: `3px solid ${ts['--theme-card-border']}` }} />
          ) : (
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
              style={{ background: ts['--theme-accent'], color: ts['--theme-accent-text'], border: `3px solid ${ts['--theme-card-border']}` }}>
              {displayName?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <div>
            <h1 className="text-lg font-bold" style={{ color: ts['--theme-text'] }}>{displayName}'s Fan Page</h1>
            <p className="text-xs" style={{ color: ts['--theme-text-muted'] }}>@{username}</p>
          </div>
        </div>

        {/* Posts feed */}
        {diaryPosts.length === 0 ? (
          <div className="p-12 text-center rounded-xl" style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }}
            data-testid="fan-page-empty">
            <p style={{ color: ts['--theme-text-muted'] }}>No posts yet</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="fan-page-feed">
            {diaryPosts.map(post => {
              const embedData = post.media_url ? getEmbedUrl(post.media_url) : null;

              return (
                <div key={post.id} className="rounded-xl overflow-hidden"
                  style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }}
                  data-testid={`fan-post-${post.id}`}>

                  {/* Text */}
                  {post.text && (
                    <div className="px-4 pt-4">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: ts['--theme-text'] }}>{post.text}</p>
                    </div>
                  )}

                  {/* Uploaded image */}
                  {(post.image_url || post.image_path) && (
                    <div className="px-4 pt-3">
                      <img src={getImageUrl(post.image_path || post.image_url)} alt="" className="rounded-lg w-full max-h-96 object-cover" />
                    </div>
                  )}

                  {/* Embedded media */}
                  {embedData && (
                    <div className="px-4 pt-3">
                      {post.media_url && (
                        <p className="text-xs font-medium mb-2" style={{ color: ts['--theme-accent'] }}>
                          {getEmbedLabel(post.media_url)}
                        </p>
                      )}
                      <div className="rounded-lg overflow-hidden"
                        style={embedData.aspectRatio ? { aspectRatio: embedData.aspectRatio } : { height: embedData.height || 300 }}>
                        <iframe src={embedData.embedUrl} title={post.text || 'Embedded content'}
                          className="w-full h-full border-0 rounded-lg"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen loading="lazy" />
                      </div>
                    </div>
                  )}

                  {/* Non-embeddable media link */}
                  {post.media_url && !embedData && (
                    <div className="px-4 pt-3">
                      <a href={post.media_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-opacity hover:opacity-80"
                        style={{ background: `${ts['--theme-accent']}15`, color: ts['--theme-accent'] }}>
                        {post.media_url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}

                  {/* Date */}
                  <div className="px-4 py-3">
                    <p className="text-xs" style={{ color: ts['--theme-text-muted'] }}>
                      {new Date(post.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-6" style={{ borderTop: `1px solid ${ts['--theme-card-border']}` }}>
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center space-x-2 opacity-60 hover:opacity-100 transition-opacity">
            <MonkeyLogo size={20} />
            <span className="text-sm font-medium" style={{ color: ts['--theme-text-muted'] }}>Powered by LinkMonkey</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FanPage;
